// MockCheckout — v0.8.0 (Dev / Mock Stripe)
//
// Fake Stripe checkout page used during development when STRIPE_MOCK=true.
// Renders a realistic-looking checkout UI and calls /api/mock-stripe-confirm
// to activate a Pro subscription in Supabase, then redirects to
// /account?upgraded=true — exactly the same flow as real Stripe.
//
// This page is a public route (/mock-checkout) but is only reachable
// via the mock-mode checkout redirect. In production, STRIPE_MOCK is false
// so stripe-checkout returns a real Stripe URL and this page is never linked.

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { API_BASE_URL } from '../services/api';

export default function MockCheckout() {
  const [params]    = useSearchParams();
  const navigate    = useNavigate();
  const cancelUrl   = params.get('cancel') || '/pricing';
  const [paying, setPaying]   = useState(false);
  const [error, setError]     = useState(null);

  async function handlePay() {
    setError(null);
    setPaying(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Please sign in to complete your purchase.');

      const res = await fetch(`${API_BASE_URL}/mock-stripe-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ daysUntilExpiry: 30 }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Payment failed.');

      // Mirror the real Stripe success redirect.
      window.location.href = '/account?upgraded=true';
    } catch (err) {
      setError(err.message);
      setPaying(false);
    }
  }

  function handleCancel() {
    window.location.href = cancelUrl;
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>

        {/* Mock badge */}
        <div style={mockBadgeStyle}>🧪 Mock Checkout — Dev Mode Only</div>

        {/* Stripe-style header */}
        <div style={logoRowStyle}>
          <div style={logoStyle}>Rentava</div>
        </div>

        <h2 style={headingStyle}>Complete your subscription</h2>

        {/* Plan summary */}
        <div style={summaryBoxStyle}>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Rentava — Pro Plan</span>
            <span style={summaryPriceStyle}>$49.00/mo</span>
          </div>
          <div style={{ ...summaryRowStyle, borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
            <span style={{ ...summaryLabelStyle, fontWeight: 700 }}>Total due today</span>
            <span style={{ ...summaryPriceStyle, fontWeight: 700 }}>$49.00</span>
          </div>
        </div>

        {/* Fake card form */}
        <div style={fieldGroupStyle}>
          <div style={fieldStyle}>
            <label style={fieldLabelStyle}>Card number</label>
            <div style={fakeInputStyle}>4242 4242 4242 4242</div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={fieldLabelStyle}>Expires</label>
              <div style={fakeInputStyle}>12 / 29</div>
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={fieldLabelStyle}>CVC</label>
              <div style={fakeInputStyle}>123</div>
            </div>
          </div>
          <div style={fieldStyle}>
            <label style={fieldLabelStyle}>Name on card</label>
            <div style={fakeInputStyle}>Test User</div>
          </div>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <button
          style={payButtonStyle(paying)}
          onClick={handlePay}
          disabled={paying}
        >
          {paying ? 'Activating subscription…' : '✓ Activate Pro Subscription'}
        </button>

        <button style={cancelButtonStyle} onClick={handleCancel} disabled={paying}>
          Cancel
        </button>

        <p style={noteStyle}>
          This is a mock checkout for development. No real payment is processed.
          Clicking "Activate" writes directly to your Supabase dev profile.
        </p>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyle = {
  minHeight: '100vh',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '36px 32px',
  maxWidth: '440px',
  width: '100%',
  boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
};

const mockBadgeStyle = {
  background: '#fef3c7',
  color: '#92400e',
  borderRadius: '8px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '20px',
};

const logoRowStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '20px',
};

const logoStyle = {
  fontSize: '18px',
  fontWeight: 800,
  color: '#111827',
};

const headingStyle = {
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  margin: '0 0 20px',
};

const summaryBoxStyle = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '16px',
  marginBottom: '24px',
};

const summaryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const summaryLabelStyle = {
  fontSize: '14px',
  color: '#374151',
};

const summaryPriceStyle = {
  fontSize: '14px',
  color: '#111827',
};

const fieldGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  marginBottom: '24px',
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const fakeInputStyle = {
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  padding: '10px 12px',
  fontSize: '14px',
  color: '#9ca3af',
  background: '#f9fafb',
  cursor: 'not-allowed',
};

const payButtonStyle = (loading) => ({
  display: 'block',
  width: '100%',
  background: loading
    ? '#9ca3af'
    : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  color: '#ffffff',
  border: 'none',
  borderRadius: '10px',
  padding: '14px',
  fontSize: '15px',
  fontWeight: 700,
  cursor: loading ? 'not-allowed' : 'pointer',
  marginBottom: '10px',
});

const cancelButtonStyle = {
  display: 'block',
  width: '100%',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: '10px',
  padding: '12px',
  fontSize: '14px',
  color: '#6b7280',
  cursor: 'pointer',
  marginBottom: '20px',
};

const errorStyle = {
  color: '#ef4444',
  fontSize: '13px',
  marginBottom: '12px',
  textAlign: 'center',
};

const noteStyle = {
  fontSize: '11px',
  color: '#9ca3af',
  textAlign: 'center',
  lineHeight: 1.5,
  margin: 0,
};
